import getDataSelector, { getByIdSelector } from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { deriveStoreStatuses } from './imageStores'

const standardPropertyWhitelist = [
  'checksum',
  'container_format',
  'created_at',
  'disk_format',
  'file',
  'id',
  'min_disk',
  'min_ram',
  'name',
  'owner',
  'protected',
  'schema',
  'self',
  'size',
  'status',
  'tags',
  'updated_at',
  'virtual_size',
  'visibility',
  'os_hidden',
  'os_hash_algo',
  'os_hash_value',
  'hw_tpm_version',
  'hw_tpm_model',
  'owner_user_name',
  'locations',
]

const prefixWhitelist = ['os_glance']

// IMGLIST-FR-064 (PCD-10120): resolve the source VM id from Nova's native instance_uuid
// property, which Nova now sets on volume-backed snapshots too.
export const resolveSourceVmId = (image) => image?.instance_uuid

const getImageType = (image) => {
  if (image?.image_type === 'snapshot') {
    return 'ephemeralDiskSnapshot'
  } else if (image?.block_device_mapping) {
    return 'vmVolumeSnapshot'
  }
  return 'image'
}

const imageTypeLabels = {
  image: 'Image',
  ephemeralDiskSnapshot: 'VM Ephemeral Disk Snapshot',
  vmVolumeSnapshot: 'VM Volume Snapshot',
}

export const imagesSelector = createSharedSelector(
  getDataSelector<DataKeys.Images>(DataKeys.Images),
  (images) => {
    return images.map((image) => {
      // any model changes go here
      let snapshotIds = []
      const otherPropertyKeys = Object.keys(image).filter(
        (key) =>
          !standardPropertyWhitelist.includes(key) &&
          !prefixWhitelist.some((prefix) => key.startsWith(prefix)),
      )
      const properties = otherPropertyKeys.reduce((accum, key) => {
        return {
          ...accum,
          [key]: image[key],
        }
      }, {})
      const imageType = getImageType(image)
      // for identifying related volume snapshots
      try {
        const blockStorage = image?.block_device_mapping
          ? JSON.parse(image.block_device_mapping)
          : null
        if (blockStorage) {
          snapshotIds = blockStorage
            .filter((vol) => vol.source_type === 'snapshot')
            .map((vol) => vol.snapshot_id)
        }
      } catch (e) {
        console.error(e)
      }
      return {
        ...image,
        imageType,
        imageTypeDisplay: imageTypeLabels[imageType],
        snapshotIds,
        properties,
        storeStatuses: deriveStoreStatuses(image),
      }
    })
  },
)

/**
 * O(1) lookup for raw images by ID from normalized cache (no transformations)
 * Use this for direct ID-based lookups of untransformed images
 * @example
 * const selectImageById = getByIdSelector(DataKeys.Images)
 * const image = useAppSelector(state => selectImageById(state)('image-123'))
 */
export const imagesByIdRaw = getByIdSelector(DataKeys.Images)

/**
 * Transformed images indexed by ID
 * Contains all image transformations (imageType, imageTypeDisplay, snapshotIds, properties)
 * Use this when you need transformed images: imagesById[imageId]
 */
export const imagesByIdSelector = createSharedSelector(imagesSelector, (images) => {
  return images.reduce((accum, img) => {
    return {
      ...accum,
      [img.id]: img,
    }
  }, {})
})

export const vtpmImagesSelector = createSharedSelector(imagesSelector, (images) => {
  return images.filter((image) => !!image?.hw_tpm_version)
})
